/**
 * Color Picker — Foraging Wheel (visual per-component customizer + tier swap)
 *
 * Components (indexed 0..3):
 *   0 = Base, 1 = Insert, 2 = Lid, 3 = Screw
 *
 * Flow:
 *   - Core swatches: all four parts match (standard variant system).
 *   - "+N colors" toggle expands the workshop palette.
 *   - "Mix your own" chip enters Custom mode: per-component coloring + line item properties.
 *   - Arrows next to Lid and Insert cycle through their Shopify variant options
 *     (e.g. "2 openings" → "4 openings"). The SVG redraws holes/compartments
 *     based on the integer parsed from the option value name.
 *
 * Key technical note: SVG fill updates use `element.style.fill = ...` (inline style),
 * NOT `setAttribute('fill', ...)` — CSS rules with `fill:` win the cascade over
 * SVG presentation attributes, so setAttribute silently fails on styled shapes.
 */

if (!customElements.get('color-picker-wheel')) {
  class ColorPickerWheel extends HTMLElement {
    constructor() {
      super();
      // Shared elements
      this.cta = null;
      this.workshopWrap = null;
      this.nameDisplay = null;
      this.upsellBtn = null;
      this.lidBuilderWrap = null;
      this.activeLabel = null;
      this.resetBtn = null;
      this.paletteContainer = null;
      this.productForm = null;

      // Wheel-specific elements
      this.wheelStack = null;
      this.lidHolesGroup = null;
      this.insertCompartmentsGroup = null;
      this.lidTierLabel = null;
      this.insertTierLabel = null;

      // State
      this.isWorkshopOpen = false;
      this.isCustomMode = false;
      this.activeComponentIdx = 0; // 0=base, 1=insert, 2=lid, 3=screw, 4=holder (cage only)
      this.componentCount = 4; // recomputed in connectedCallback from baseVariant

      // Per-component colors (index 0..3)
      this.componentColors = [];
      this.defaultColor = { name: 'Grey', rgb: 'rgb(158, 158, 158)' };

      // Tier state
      this.lidVariants = [];      // [{value, count, radioId}]
      this.insertVariants = [];   // [{value, count, radioId}]
      this.lidTierIdx = 0;
      this.insertTierIdx = 0;

      // Labels (from data attributes, locale-aware)
      this.componentLabels = ['Base', 'Insert', 'Lid', 'Screw'];

      // Layout
      this.baseVariant = 'standard';
    }

    connectedCallback() {
      // Core elements
      this.cta = this.querySelector('[data-cta]');
      this.workshopWrap = this.querySelector('[data-workshop-wrap]');
      this.nameDisplay = this.querySelector('[data-selected-name]');
      this.upsellBtn = this.querySelector('[data-upsell-cta]');
      this.lidBuilderWrap = this.querySelector('[data-lid-builder-wrap]');
      this.activeLabel = this.querySelector('[data-active-label]');
      this.resetBtn = this.querySelector('[data-toy-reset]');
      this.paletteContainer = this.querySelector('[data-lid-palette]');

      // Wheel elements
      this.wheelStack = this.querySelector('[data-wheel-stack]');
      this.lidHolesGroup = this.querySelector('[data-lid-holes]');
      this.insertCompartmentsGroup = this.querySelector('[data-insert-compartments]');
      this.lidTierLabel = this.querySelector('[data-tier-label="lid"] [data-tier-label-text]');
      this.insertTierLabel = this.querySelector('[data-tier-label="insert"] [data-tier-label-text]');

      // Product form
      const formId = this.dataset.productFormId;
      if (formId) {
        this.productForm = document.getElementById(formId);
      }

      this.baseVariant = this.dataset.baseVariant || 'standard';

      // Locale labels — order matches activeComponentIdx (0=base..4=holder)
      this.componentLabels = [
        this.dataset.labelBase || 'Base',
        this.dataset.labelInsert || 'Insert',
        this.dataset.labelLid || 'Lid',
        this.dataset.labelScrew || 'Screw',
      ];
      // Cage-mount product has a 5th component: the Holder.
      if (this.baseVariant === 'cage') {
        this.componentLabels.push(this.dataset.labelHolder || 'Holder');
        this.componentCount = 5;
      }

      // Read tier variant data from hidden radios
      this.collectTierVariants();

      // Read default color from current selection
      const currentName = this.nameDisplay?.textContent?.trim() || 'Grey';
      const currentRgb = this.getColorRgbByName(currentName);
      this.defaultColor = { name: currentName, rgb: currentRgb };

      // Initialize per-component colors to the default
      this.componentColors = Array.from({ length: this.componentCount }, () => ({
        name: this.defaultColor.name,
        rgb: this.defaultColor.rgb,
      }));

      // Event wiring
      if (this.cta && this.workshopWrap) {
        this.cta.addEventListener('click', this.toggleWorkshop.bind(this));
      }
      if (this.upsellBtn) {
        this.upsellBtn.addEventListener('click', this.toggleCustomMode.bind(this));
      }
      if (this.wheelStack) {
        this.wheelStack.addEventListener('click', this.handleWheelClick.bind(this));
      }
      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', this.handleReset.bind(this));
      }
      if (this.paletteContainer) {
        this.paletteContainer.addEventListener('click', this.handlePaletteClick.bind(this));
      }
      // Tier arrows
      this.querySelectorAll('[data-tier-prev], [data-tier-next]').forEach((btn) => {
        btn.addEventListener('click', this.handleTierArrow.bind(this));
      });
      // Close button on the custom-mode header
      const closeBtn = this.querySelector('[data-close-custom]');
      if (closeBtn) {
        closeBtn.addEventListener('click', this.exitCustomMode.bind(this));
      }

      // Unified swatch handler (core + workshop)
      this.addEventListener('click', this.handleSwatchClick.bind(this));

      // Initial render
      this.renderLidHoles();
      this.renderInsertCompartments();
      this.updateTierLabels();

      // Restore states from product-info container
      this.restoreOpenState();
    }

    // ---- Tier discovery & rendering ----

    /**
     * Walks the hidden tier radios and builds the lidVariants / insertVariants
     * arrays. Parses an integer count from each option value name
     * (e.g. "2 openings" → 2, "4 compartments" → 4).
     */
    collectTierVariants() {
      const parseCount = (str) => {
        const m = String(str).match(/(\d+)/);
        return m ? parseInt(m[1], 10) : 0;
      };

      const lidRadios = this.querySelectorAll('input[data-tier="lid"]');
      lidRadios.forEach((r, i) => {
        const v = {
          value: r.dataset.tierValue || r.value,
          count: parseCount(r.value),
          radioId: r.id,
          element: r,
        };
        this.lidVariants.push(v);
        if (r.checked) this.lidTierIdx = i;
      });

      const insertRadios = this.querySelectorAll('input[data-tier="insert"]');
      insertRadios.forEach((r, i) => {
        const v = {
          value: r.dataset.tierValue || r.value,
          count: parseCount(r.value),
          radioId: r.id,
          element: r,
        };
        this.insertVariants.push(v);
        if (r.checked) this.insertTierIdx = i;
      });
    }

    /**
     * Hole positions for a lid with N openings.
     * Uniform hole radius - within a lid, every opening is the same size.
     * Returns {x, y, r} relative to the lid centre (120, 65) in viewBox coords.
     */
    lidHolePattern(count) {
      const cx = 120, cy = 65;
      const holeR = 13; // uniform across all tier counts
      if (count <= 0) return [];
      if (count === 1) {
        return [{ x: cx, y: cy - 28, r: holeR }];
      }
      if (count === 2) {
        return [
          { x: cx, y: cy - 28, r: holeR },
          { x: cx, y: cy + 28, r: holeR },
        ];
      }
      // 3+ openings: distribute evenly around a ring.
      const holes = [];
      const ringR = 32;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count - Math.PI / 2;
        holes.push({
          x: cx + Math.cos(angle) * ringR,
          y: cy + Math.sin(angle) * ringR,
          r: holeR,
        });
      }
      return holes;
    }

    /**
     * Compartment pattern for an insert with N compartments.
     * The standard insert is a divider STRUCTURE (bars meeting at a centre hub),
     * not a disc with holes - see the reference renders:
     *   1 = single offset bar
     *   2 = single horizontal bar (splits into top + bottom)
     *   3 = T-shape (vertical top half + horizontal across)
     *   4 = cross (horizontal + vertical bars)
     * Returns { hub: {cx, cy, r}, bars: [{x, y, w, h, rotate?, ox?, oy?}, ...] }.
     */
    insertCompartmentPattern(count) {
      const cage = this.baseVariant === 'cage';
      const cx = 120;
      const cy = cage ? 32 : 65;
      const radius = cage ? 52 : 54;
      const barW = 9;
      const hubR = 11;

      if (count <= 0) return { hub: null, bars: [] };

      if (cage) {
        // Cage = D-shape. Bars radiate from the centre into the lower semicircle only.
        const bars = [];
        const armLen = radius - hubR + 2;
        if (count === 1 || count === 2) {
          bars.push({ x: cx - barW / 2, y: cy, w: barW, h: armLen });
        } else if (count === 3) {
          // Y-shape: 2 angled bars + 1 straight down
          [Math.PI * 0.30, Math.PI * 0.70].forEach((a) => {
            bars.push({
              x: cx, y: cy, w: armLen, h: barW,
              rotate: (a * 180) / Math.PI, ox: cx, oy: cy,
            });
          });
          bars.push({ x: cx - barW / 2, y: cy, w: barW, h: armLen });
        } else {
          // 4+: evenly-spaced arms in the lower 180 degrees.
          const arms = count - 1;
          for (let i = 0; i < arms; i++) {
            const a = Math.PI * (i + 1) / (arms + 1);
            bars.push({
              x: cx, y: cy, w: armLen, h: barW,
              rotate: (a * 180) / Math.PI, ox: cx, oy: cy,
            });
          }
        }
        return { hub: { cx, cy, r: hubR }, bars };
      }

      // Standard round insert.
      const bars = [];
      const armLen = radius - hubR + 2;
      if (count === 1) {
        // Single horizontal bar across.
        bars.push({ x: cx - armLen, y: cy - barW / 2, w: armLen * 2, h: barW });
      } else if (count === 2) {
        bars.push({ x: cx - armLen, y: cy - barW / 2, w: armLen * 2, h: barW });
      } else if (count === 3) {
        // T: horizontal + vertical going UP only
        bars.push({ x: cx - armLen, y: cy - barW / 2, w: armLen * 2, h: barW });
        bars.push({ x: cx - barW / 2, y: cy - armLen, w: barW, h: armLen });
      } else {
        // 4+: full cross
        bars.push({ x: cx - armLen, y: cy - barW / 2, w: armLen * 2, h: barW });
        bars.push({ x: cx - barW / 2, y: cy - armLen, w: barW, h: armLen * 2 });
      }
      return { hub: { cx, cy, r: hubR }, bars };
    }

    renderLidHoles() {
      if (!this.lidHolesGroup) return;
      const tier = this.lidVariants[this.lidTierIdx];
      const count = tier ? tier.count : 2;
      const holes = this.lidHolePattern(count);
      const NS = 'http://www.w3.org/2000/svg';
      this.lidHolesGroup.innerHTML = '';
      holes.forEach((h) => {
        const el = document.createElementNS(NS, 'circle');
        el.setAttribute('cx', h.x);
        el.setAttribute('cy', h.y);
        el.setAttribute('r', h.r);
        el.setAttribute('class', 'color-picker__wheel-hole');
        this.lidHolesGroup.appendChild(el);
      });
    }

    renderInsertCompartments() {
      if (!this.insertCompartmentsGroup) return;
      const tier = this.insertVariants[this.insertTierIdx];
      const count = tier ? tier.count : 4;
      const NS = 'http://www.w3.org/2000/svg';
      this.insertCompartmentsGroup.innerHTML = '';

      const { hub, bars } = this.insertCompartmentPattern(count);

      // For the standard variant the insert HAS no outer disc - the bars + hub
      // ARE the colorable surface, so they get data-shape-fill. For the cage
      // variant the outer D-shape carries data-shape-fill and the bars below
      // also pick up the same colour for visual cohesion.
      const initialFill = this.componentColors[1]?.rgb;
      bars.forEach((b) => {
        const el = document.createElementNS(NS, 'rect');
        el.setAttribute('x', b.x);
        el.setAttribute('y', b.y);
        el.setAttribute('width', b.w);
        el.setAttribute('height', b.h);
        el.setAttribute('rx', 2);
        el.setAttribute('class', 'color-picker__wheel-insert-bar');
        el.setAttribute('data-shape-fill', '');
        if (initialFill) el.style.fill = initialFill;
        if (typeof b.rotate === 'number') {
          el.setAttribute('transform', `rotate(${b.rotate} ${b.ox} ${b.oy})`);
        }
        this.insertCompartmentsGroup.appendChild(el);
      });

      if (hub) {
        const el = document.createElementNS(NS, 'circle');
        el.setAttribute('cx', hub.cx);
        el.setAttribute('cy', hub.cy);
        el.setAttribute('r', hub.r);
        el.setAttribute('class', 'color-picker__wheel-insert-hub');
        el.setAttribute('data-shape-fill', '');
        if (initialFill) el.style.fill = initialFill;
        this.insertCompartmentsGroup.appendChild(el);
      }
    }

    updateTierLabels() {
      if (this.lidTierLabel) {
        const t = this.lidVariants[this.lidTierIdx];
        this.lidTierLabel.textContent = t ? t.value : '';
      }
      if (this.insertTierLabel) {
        const t = this.insertVariants[this.insertTierIdx];
        this.insertTierLabel.textContent = t ? t.value : '';
      }
    }

    // ---- Tier arrow handler ----

    handleTierArrow(e) {
      const btn = e.currentTarget;
      if (btn.disabled) return;
      const part = btn.dataset.tierPrev || btn.dataset.tierNext;
      const dir = btn.dataset.tierNext ? 1 : -1;
      // Stash per-component colors BEFORE we fire the variant change -
      // Shopify will re-render this whole element and the new instance reads
      // state back from product-info dataset in restoreComponentState().
      this.persistComponentState();
      if (part === 'lid') {
        if (this.lidVariants.length < 2) return;
        this.lidTierIdx = (this.lidTierIdx + dir + this.lidVariants.length) % this.lidVariants.length;
        this.renderLidHoles();
        this.fireTierVariantChange('lid');
      } else if (part === 'insert') {
        if (this.insertVariants.length < 2) return;
        this.insertTierIdx = (this.insertTierIdx + dir + this.insertVariants.length) % this.insertVariants.length;
        this.renderInsertCompartments();
        this.fireTierVariantChange('insert');
      }
      this.updateTierLabels();
      this.updateWheelVisual(); // re-apply colors after redraw
    }

    fireTierVariantChange(part) {
      const list = part === 'lid' ? this.lidVariants : this.insertVariants;
      const idx = part === 'lid' ? this.lidTierIdx : this.insertTierIdx;
      const target = list[idx];
      if (!target || !target.element) return;
      if (!target.element.checked) {
        target.element.checked = true;
        target.element.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // ---- Workshop open/close (same pattern as foraging toy) ----

    restoreOpenState() {
      const productInfo = this.closest('product-info');
      if (productInfo && productInfo.dataset.workshopOpen === 'true') {
        if (this.workshopWrap) {
          this.workshopWrap.classList.add('is-open', 'no-animate');
          this.isWorkshopOpen = true;
        }
        if (this.cta) {
          this.cta.classList.add('is-open');
          const openText = this.cta.dataset.textOpen;
          if (openText) {
            const textEl = this.cta.querySelector('[data-cta-text]');
            if (textEl) textEl.textContent = openText;
          }
        }
      }
      if (productInfo && productInfo.dataset.customMode === 'true') {
        this.enterCustomMode(false);
        // After enterCustomMode the default colors are set; now overlay the
        // per-component colors the previous instance stashed before its tier
        // change.
        if (this.restoreComponentState()) {
          this.updateWheelVisual();
          this.updatePaletteSelection();
          this.updateLineItemProperties();
        }
      }
    }

    persistOpenState(isOpen) {
      const productInfo = this.closest('product-info');
      if (productInfo) {
        productInfo.dataset.workshopOpen = isOpen ? 'true' : 'false';
      }
    }

    persistCustomMode(isCustom) {
      const productInfo = this.closest('product-info');
      if (productInfo) {
        productInfo.dataset.customMode = isCustom ? 'true' : 'false';
      }
    }

    /**
     * Serialize per-component colors + active index to the product-info
     * container. The tier arrows fire a Shopify variant change which
     * re-renders this whole element, so we have to stash state somewhere
     * the new instance can read it back from.
     */
    persistComponentState() {
      const productInfo = this.closest('product-info');
      if (!productInfo) return;
      if (this.isCustomMode) {
        try {
          productInfo.dataset.wheelComponentColors = JSON.stringify(this.componentColors);
          productInfo.dataset.wheelActiveIdx = String(this.activeComponentIdx);
        } catch (e) {}
      } else {
        // Not in custom mode -> nothing to remember.
        delete productInfo.dataset.wheelComponentColors;
        delete productInfo.dataset.wheelActiveIdx;
      }
    }

    /**
     * Read back per-component colors stashed on product-info after a
     * variant re-render. Returns true if anything was restored.
     */
    restoreComponentState() {
      const productInfo = this.closest('product-info');
      if (!productInfo || !productInfo.dataset.wheelComponentColors) return false;
      try {
        const saved = JSON.parse(productInfo.dataset.wheelComponentColors);
        if (Array.isArray(saved) && saved.length >= this.componentCount) {
          // Trim to current componentCount (cage vs standard).
          this.componentColors = saved.slice(0, this.componentCount).map((c) => ({
            name: c?.name || this.defaultColor.name,
            rgb:  c?.rgb  || this.defaultColor.rgb,
          }));
        }
        if (productInfo.dataset.wheelActiveIdx) {
          const idx = parseInt(productInfo.dataset.wheelActiveIdx, 10);
          if (!isNaN(idx) && idx >= 0 && idx < this.componentCount) {
            this.activeComponentIdx = idx;
          }
        }
        return true;
      } catch (e) {
        return false;
      }
    }

    toggleWorkshop() {
      if (this.workshopWrap.classList.contains('no-animate')) {
        this.workshopWrap.classList.remove('no-animate');
      }
      this.isWorkshopOpen = this.workshopWrap.classList.toggle('is-open');
      this.cta.classList.toggle('is-open', this.isWorkshopOpen);
      this.persistOpenState(this.isWorkshopOpen);

      const openText = this.cta.dataset.textOpen;
      const closedText = this.cta.dataset.textClosed;
      const textEl = this.cta.querySelector('[data-cta-text]');
      if (textEl) textEl.textContent = this.isWorkshopOpen ? openText : closedText;

      if (this.isWorkshopOpen) {
        const swatches = this.workshopWrap.querySelectorAll('.color-picker__named-swatch');
        swatches.forEach((s) => {
          s.style.animation = 'none';
          void s.offsetHeight;
          s.style.animation = '';
        });
      }
    }

    // ---- Standard swatch click (core + workshop, all components match) ----

    handleSwatchClick(e) {
      const btn = e.target.closest('[data-color-swatch]');
      if (!btn) return;
      if (btn.closest('[data-lid-palette]')) return;

      const colorName = btn.dataset.colorName;
      const radioId = btn.dataset.radioTarget;
      if (!colorName || !radioId) return;

      if (this.isCustomMode) {
        this.exitCustomMode();
      }

      this.querySelectorAll('.is-selected').forEach((el) => el.classList.remove('is-selected'));
      btn.classList.add('is-selected');

      if (this.nameDisplay) {
        this.nameDisplay.textContent = colorName;
      }

      const rgb = this.getColorRgbByName(colorName);
      this.defaultColor = { name: colorName, rgb };
      this.componentColors = Array.from({ length: this.componentCount }, () => ({
        name: colorName, rgb,
      }));

      this.clearLineItemProperties();

      if (this.workshopWrap && this.workshopWrap.classList.contains('is-open')) {
        this.persistOpenState(true);
      }

      const radio = this.querySelector('#' + CSS.escape(radioId));
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // ---- Custom mode ----

    toggleCustomMode() {
      if (this.isCustomMode) this.exitCustomMode();
      else this.enterCustomMode(true);
    }

    enterCustomMode(animate = true) {
      this.isCustomMode = true;
      this.activeComponentIdx = 0;
      this.classList.add('is-custom-mode-active');
      if (this.upsellBtn) this.upsellBtn.classList.add('is-active');
      if (this.lidBuilderWrap) this.lidBuilderWrap.classList.add('is-open');
      this.persistCustomMode(true);
      this.updateWheelVisual();
      this.updatePaletteSelection();
      this.switchToCustomVariant();
      this.updateLineItemProperties();
    }

    exitCustomMode() {
      this.isCustomMode = false;
      this.classList.remove('is-custom-mode-active');
      if (this.upsellBtn) this.upsellBtn.classList.remove('is-active');
      if (this.lidBuilderWrap) this.lidBuilderWrap.classList.remove('is-open');
      this.persistCustomMode(false);
      // Wipe stashed component colors so the next re-render doesn't restore them.
      this.persistComponentState();
      this.clearLineItemProperties();
      this.switchToStandardVariant();
    }

    // ---- Wheel SVG click ----

    handleWheelClick(e) {
      const group = e.target.closest('[data-wheel-component]');
      if (!group) return;
      // Ignore clicks on arrow buttons that may bubble up.
      if (e.target.closest('[data-tier-prev], [data-tier-next]')) return;
      this.activeComponentIdx = parseInt(group.dataset.wheelComponent, 10);
      this.updateWheelVisual();
      this.updatePaletteSelection();
    }

    updateWheelVisual() {
      // Update fills via style.fill so CSS doesn't beat us.
      const fills = this.querySelectorAll('[data-wheel-component]');
      fills.forEach((g) => {
        const idx = parseInt(g.dataset.wheelComponent, 10);
        const color = this.componentColors[idx];
        if (!color) return;
        // Colorable elements: every node tagged data-shape-fill, plus the
        // legacy lid/base/insert/screw classes for backwards-compat.
        const colorables = g.querySelectorAll(
          '[data-shape-fill], .color-picker__wheel-lid, .color-picker__wheel-base, .color-picker__wheel-insert, .color-picker__wheel-screw'
        );
        colorables.forEach((el) => { el.style.fill = color.rgb; });
        // Active ring toggle
        g.classList.toggle('is-active', idx === this.activeComponentIdx);
      });

      // Active label
      if (this.activeLabel) {
        this.activeLabel.textContent = this.componentLabels[this.activeComponentIdx] || '';
      }
    }

    // ---- Reset ----

    handleReset() {
      this.componentColors = Array.from({ length: this.componentCount }, () => ({
        name: this.defaultColor.name,
        rgb: this.defaultColor.rgb,
      }));
      this.activeComponentIdx = 0;
      this.updateWheelVisual();
      this.updatePaletteSelection();
      this.updateLineItemProperties();
    }

    // ---- Per-component palette ----

    handlePaletteClick(e) {
      const btn = e.target.closest('[data-lid-color]');
      if (!btn) return;
      const colorName = btn.dataset.lidColor;
      const colorRgb = btn.dataset.lidColorRgb;
      if (!colorName) return;

      this.componentColors[this.activeComponentIdx] = { name: colorName, rgb: colorRgb };

      // Auto-advance: 0 (base) → 1 (insert) → 2 (lid) → 3 (screw) → stop
      if (this.activeComponentIdx < this.componentCount - 1) {
        this.activeComponentIdx++;
      }

      this.updateWheelVisual();
      this.updatePaletteSelection();
      this.updateLineItemProperties();
    }

    updatePaletteSelection() {
      if (!this.paletteContainer) return;
      const currentColor = this.componentColors[this.activeComponentIdx]?.name;
      const swatches = this.paletteContainer.querySelectorAll('[data-lid-color]');
      swatches.forEach((s) => {
        s.classList.toggle('is-selected', s.dataset.lidColor === currentColor);
      });
    }

    // ---- Variant switching ----

    switchToCustomVariant() {
      const customRadio = this.querySelector('[data-variant-type="custom"]');
      if (customRadio && !customRadio.checked) {
        customRadio.checked = true;
        customRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    switchToStandardVariant() {
      // Next swatch click will fire the appropriate radio change.
    }

    // ---- Line item properties ----

    updateLineItemProperties() {
      if (!this.productForm) return;
      // Use locale-aware labels as keys (e.g. "Base: Purple", "Inzet: Groen").
      for (let i = 0; i < this.componentCount; i++) {
        this.setLineItemProperty(this.componentLabels[i], this.componentColors[i]?.name || '');
      }
    }

    clearLineItemProperties() {
      if (!this.productForm) return;
      for (let i = 0; i < this.componentCount; i++) {
        this.setLineItemProperty(this.componentLabels[i], '');
      }
    }

    setLineItemProperty(name, value) {
      if (!this.productForm || !name) return;
      const inputName = `properties[${name}]`;
      let input = this.productForm.querySelector(`input[name="${inputName}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        this.productForm.appendChild(input);
      }
      input.value = value;
    }

    // ---- Helpers ----

    getColorRgbByName(name) {
      if (!this.paletteContainer) return 'rgb(158, 158, 158)';
      const btn = this.paletteContainer.querySelector(`[data-lid-color="${name}"]`);
      if (btn && btn.dataset.lidColorRgb) {
        return btn.dataset.lidColorRgb;
      }
      return 'rgb(158, 158, 158)';
    }
  }

  customElements.define('color-picker-wheel', ColorPickerWheel);
}
